import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack, Typography } from '@mui/material';

import { CanvasContext } from '@/designs-dev/handover/Canvas';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import CopyForClaude, {
  COPY_FOR_CLAUDE_CLASS,
} from '@/designs-dev/loft/CopyForClaude';
import { handoverPointer } from '@/designs-dev/loft/command';

export type MiniScreenVariant = 'phone' | 'desktop';

// Desktop frames render a full admin page, so they get a wider canvas at a
// smaller scale to land at a similar thumbnail size to the phone.
//
// The scales are the readability dial for the whole handover. They were 0.5 and
// 0.3, which fitted more screens across a row than anybody could read the copy
// in: a handover is looked at to judge words and states, so a row that scrolls is
// a cheaper cost than a frame you have to zoom into.
//
// Desktop went 0.42 to 0.6 for the same reason: a control panel holds three
// columns of small type, so it was the frame you still had to lean into.
const DIMS: Record<
  MiniScreenVariant,
  { width: number; height: number; scale: number }
> = {
  phone: { width: 375, height: 812, scale: 0.65 },
  desktop: { width: 1440, height: 900, scale: 0.6 },
};

// The rendered size of a mini, for callers that have to line something up beside
// one — the arrow between two steps of a flow, say. Derived here so a change to
// the scales above carries to them instead of leaving a hardcoded offset behind.
export const miniSize = (variant: MiniScreenVariant) => {
  const { width, height, scale } = DIMS[variant];
  return { width: width * scale, height: height * scale };
};

// A screen rendered small and live for the handover page. Provides its own
// PhoneScreenContext so bottom sheets and pushed pages portal inside the mini
// frame rather than escaping it.
//
// Each mini is a real screen, not a picture of one, so a flows page renders
// dozens of full prototypes at once: the Project MC page is eleven thousand DOM
// nodes. That was survivable while these pages sat behind a list nobody clicked
// through, and stopped being survivable when flows became the first thing a
// project opens — every commit of that page happens in one go, which is the
// 1.7s the console was complaining about.
//
// So a mini renders when it comes near the viewport and holds its exact size
// until then. The page arrives at a few hundred nodes and fills in as you
// scroll, and because the box is the right size from the start, nothing moves
// under you while it does.
const MiniScreen = ({
  label,
  caption,
  variant = 'phone',
  pointer,
  isNew = false,
  tint,
  children,
}: {
  label: string;
  /** The label in this colour: which group it belongs to. */
  tint?: string;
  /** Title in red: new since the last look. See FlowStep.isNew. */
  isNew?: boolean;
  caption?: string;
  variant?: MiniScreenVariant;
  /**
   * What to call this mini when pointing Claude at it. Adds a copy button beside
   * the label — the label itself is passed through, so a caller only says which
   * kind of mini this is, which group it sits in, and the id the spec keys the
   * render by.
   */
  pointer?: {
    kind: 'Step' | 'State' | 'Pattern' | 'Interaction';
    id: string;
    group?: string;
  };
  children: ReactNode;
}) => {
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);
  const canvas = useContext(CanvasContext);
  const { width, height, scale } = DIMS[variant];

  useEffect(() => {
    const el = frameRef.current;
    if (!el || shown) return;
    // A generous margin, so a mini is already rendered by the time it is
    // scrolled to rather than appearing after it.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setShown(true);
      },
      // On a canvas, the canvas: it clips what is outside it, and a margin
      // only reaches past the clip of the root it is set on.
      { root: canvas?.root ?? null, rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shown, canvas]);

  return (
    <Stack
      sx={{
        width: width * scale,
        flexShrink: 0,
        // Quiet until you are looking at this mini. Focus keeps it reachable
        // without a mouse, since opacity 0 still takes tab focus.
        [`& .${COPY_FOR_CLAUDE_CLASS}`]: {
          opacity: 0,
          transition: 'opacity .12s',
        },
        [`&:hover .${COPY_FOR_CLAUDE_CLASS}, & .${COPY_FOR_CLAUDE_CLASS}:focus-visible`]:
          { opacity: 1 },
      }}
      spacing={1}
    >
      <Box
        sx={{
          width: width * scale,
          height: height * scale,
          border: 1,
          borderColor: 'custom.neutral.200',
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
        ref={frameRef}
      >
        {shown && (
          <Box
            ref={setScreenEl}
            // Live, not a picture. Clicks used to be swallowed on capture so a
            // mini could not answer the question it was showing — a flows page
            // was a contact sheet and a tap on one meant "look closer". That
            // made every interaction on this page dead, including the ones the
            // page exists to show: a control that only does something when you
            // press it cannot be judged from a still of it. A mini is its own
            // instance, so operating one changes nothing anywhere else.
            sx={{
              position: 'relative',
              width: width,
              height: height,
              overflow: 'hidden',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              pointerEvents: 'auto',
            }}
          >
            <PhoneScreenContext.Provider value={screenEl}>
              {children}
            </PhoneScreenContext.Provider>
          </Box>
        )}
      </Box>
      <Box>
        <Stack
          sx={{ alignItems: 'center' }}
          direction='row'
          spacing={1}
          useFlexGap
        >
          <Typography
            // The group's colour wins over new's red: telling the groups
            // apart is the point of setting one.
            sx={tint || isNew ? { color: tint ?? 'error.main' } : undefined}
            variant='subtitle2'
          >
            {label}
          </Typography>
          {pointer && (
            <CopyForClaude
              text={() => handoverPointer({ ...pointer, label })}
            />
          )}
        </Stack>
        {caption && (
          <Typography
            sx={{ display: 'block', mt: 0.25 }}
            variant='caption'
            color='text.secondary'
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export default MiniScreen;
